// src/iap/initPurchases.ts
import axios from 'axios';
import * as appActions from '../Actions/app';
declare global {
  interface Window {
    CdvPurchase: unknown;
    cordova: unknown;
    Capacitor: unknown;
    document?: Document;
    __iapInitialized?: boolean;
    __iapLastVerifiedReceiptHash?: string;
    __iapVerifyArmedUntil?: number;
    __iapVerifyArmedProductId?: string;
  }
}

type MinimalUser = { id?: string | number; email?: string | null } | null;
type Dispatch = (a: unknown) => void;

const LOG = '[IAP]';
const log = (...a: unknown[]) => console.log(LOG, ...a);
const warn = (...a: unknown[]) => console.warn(LOG, ...a);
const err = (...a: unknown[]) => console.error(LOG, ...a);

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
const IAP_VERIFY_ARM_WINDOW_MS = 15 * 60 * 1000;

export function armIAPReceiptVerification(productId: string) {
  const expiresAt = Date.now() + IAP_VERIFY_ARM_WINDOW_MS;
  window.__iapVerifyArmedUntil = expiresAt;
  window.__iapVerifyArmedProductId = productId;
  log('verify arm set for product', productId, 'until', new Date(expiresAt).toISOString());
}

export function clearIAPReceiptVerificationArm() {
  window.__iapVerifyArmedUntil = undefined;
  window.__iapVerifyArmedProductId = undefined;
}

function isReceiptVerificationArmed(productIds: string[]): boolean {
  const armedUntil = window.__iapVerifyArmedUntil ?? 0;
  if (!armedUntil) return false;

  if (Date.now() > armedUntil) {
    log('verify arm expired, clearing arm');
    clearIAPReceiptVerificationArm();
    return false;
  }

  const armedProductId = window.__iapVerifyArmedProductId;
  if (armedProductId && productIds.length > 0 && !productIds.includes(armedProductId)) {
    log('verify arm mismatch, skipping verify', {
      armedProductId,
      approvedProductIds: productIds,
    });
    return false;
  }

  return true;
}

const looksB64 = (s: unknown) =>
  typeof s === 'string' && s.length > 200 && /^[A-Za-z0-9+/=]+$/.test(s);

function pickB64(obj: unknown): string | undefined {
  if (!obj) return;
  if (looksB64(obj)) return obj;
  const keys = [
    'appStoreReceipt',
    'appStoreReceiptBase64',
    'latestReceipt',
    'receipt',
    'receipt_data',
    'base64',
    'data',
    'raw',
  ];
  const o = obj as Record<string, unknown>;
  for (const k of keys) if (looksB64(o?.[k])) return o[k] as string;
  for (const v of Object.values(o)) if (looksB64(v)) return v as string;
  return;
}

// Tiny cheap hash for dedupe (NOT cryptographic; just for “don’t spam verify”).
function cheapHash(s: string): string {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h += (h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24);
  }
  // unsigned
  return (h >>> 0).toString(16);
}

function getAppReceiptViaExec(): Promise<string | undefined> {
  return new Promise((resolve) => {
    const exec = window?.cordova?.exec;
    if (!exec) {
      warn('cordova.exec not available');
      return resolve(undefined);
    }
    log('exec → InAppPurchase.appStoreReceipt');
    exec(
      (res: unknown) => {
        const b64 = pickB64(res);
        log('exec ← appStoreReceipt:', b64 ? `ok(len=${b64.length})` : `no-b64`);
        resolve(b64);
      },
      (e: unknown) => {
        warn('exec appStoreReceipt failed', e);
        resolve(undefined);
      },
      'InAppPurchase',
      'appStoreReceipt',
      [],
    );
  });
}

async function getReceiptB64(Cdv: Record<string, unknown>): Promise<string | undefined> {
  const { store, Platform } = Cdv;
  // Preferred: v13+ getter
  if (typeof store?.getReceipt === 'function') {
    try {
      const r = await store.getReceipt(Platform.APPLE_APPSTORE);
      const b64 = pickB64(r);
      log('store.getReceipt →', b64 ? `ok(len=${b64.length})` : 'no-b64');
      if (b64) return b64;
    } catch (e) {
      warn('store.getReceipt threw', e);
    }
  } else {
    log('store.getReceipt is undefined (JS bundled incorrectly or old adapter).');
  }
  // Fallback: call native directly
  return getAppReceiptViaExec();
}

async function verify(
  base64: string,
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
  meta?: { reason?: 'approved'; txId?: string; origTxId?: string },
) {
  try {
    const token = (await currentAuthToken?.()) || '';
    if (!token) {
      warn('verify skipped: no auth token');
      return;
    }

    // Dedupe per receipt blob
    const h = cheapHash(base64);
    if (window.__iapLastVerifiedReceiptHash === h) {
      log('verify skipped: same receipt hash already verified');
      return;
    }
    window.__iapLastVerifiedReceiptHash = h;

    const url = `${baseUrl}receipts/verify`;
    const { data, status } = await axios.post(
      url,
      {
        platform: 'apple',
        receipt_data: base64,
        reason: meta?.reason,
        tx_id: meta?.txId,
        original_tx_id: meta?.origTxId,
      },
      { headers: { Authorization: `Bearer ${token}` } },
    );

    log('verify ←', status, 'active=', !!data?.active, 'env=', data?.environment);
    if (data?.active) {
      dispatch(
        appActions.setUserData({
          ...(userData || {}),
          subscription_status: 'active',
        }),
      );
    } else {
      log('verify returned inactive receipt; skipping local subscription mutation');
    }
  } catch (e: unknown) {
    const errObj = e as { response?: { status?: number }; message?: string };
    err('verify failed', errObj?.response?.status, errObj?.message);
  }
}

function onDeviceReady(cb: () => void) {
  if ((window as Window & { cordova?: unknown }).cordova) {
    document.addEventListener('deviceready', cb, { once: true });
  } else {
    cb();
  }
}

export function setupIAP(
  userData: MinimalUser,
  dispatch: Dispatch,
  currentAuthToken?: () => Promise<string | undefined>,
) {
  if (window.__iapInitialized) {
    log('already initialized');
    return;
  }
  window.__iapInitialized = true;

  onDeviceReady(async () => {
    const Cdv = window.CdvPurchase;
    if (!Cdv?.store) {
      err('CdvPurchase.store missing');
      return;
    }

    const { store, ProductType, Platform, LogLevel } = Cdv;
    try {
      store.verbosity = LogLevel.DEBUG;
    } catch {
      // intentionally ignore
    }

    store.applicationUsername = () => (userData?.id ?? userData?.email ?? '') + '';

    store.register([
      {
        id: 'com.museflow.monthly',
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
      {
        id: 'com.museflow.3months',
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
      {
        id: 'com.museflow.annual',
        type: ProductType.PAID_SUBSCRIPTION,
        platform: Platform.APPLE_APPSTORE,
        group: 'museflow',
      },
    ]);

    store.when().approved(async (tx: Record<string, unknown>) => {
      const txProductIds = ((tx?.products as { id: string }[]) || []).map((p) => p.id);
      log('approved:', {
        txId: tx?.transactionId,
        orig: tx?.originalTransactionId,
        plat: tx?.platform,
        products: txProductIds,
      });

      try {
        if (!isReceiptVerificationArmed(txProductIds)) {
          log('approved: skipping verify (not armed by user purchase action)');
        } else {
          // Prefer receipt attached to tx, else pull app receipt
          let b64 = pickB64(tx?.receipt);
          log('approved: tx.receipt present?', !!b64);
          if (!b64) b64 = await getReceiptB64(Cdv);

          if (b64) {
            await verify(b64, userData, dispatch, currentAuthToken, {
              reason: 'approved',
              txId: tx?.transactionId,
              origTxId: tx?.originalTransactionId,
            });
          } else {
            warn('approved: no app receipt available to verify');
          }
          clearIAPReceiptVerificationArm();
        }
      } finally {
        try {
          tx.finish();
          log('approved: finish()');
        } catch (e) {
          err('finish() failed', e);
        }
        try {
          await store.update();
          log('approved: store.update()');
        } catch {
          // intentionally ignore
        }
      }
    });

    store.error((e: unknown) => err('Store Error:', e));
    store.ready(() => log('Store ready'));

    try {
      log('initialize([APPLE])');
      await store.initialize([Platform.APPLE_APPSTORE]);
      log('update()');
      await store.update();

      // IMPORTANT: removed cold-start verification
      // We only verify on actual approved transactions.
      log('startup: skipping verify (only verifying on purchase approval)');
    } catch (e) {
      err('initialize/update failed', e);
    }
  });
}
